import React, { useState, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { CSG } from 'three-csg-ts';
import colorsData from '../data/colors.json';
import fontsData from '../data/fonts.json';
import * as amplitude from '@amplitude/analytics-browser';

const StlGenerator = ({ theme }) => {
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState(2);
  const [thickness, setThickness] = useState(1);
  const [keyringHole, setKeyringHole] = useState(true);
  const [border, setBorder] = useState(true);
  const [borderSize, setBorderSize] = useState(0.5);
  const [textColor, setTextColor] = useState(colorsData.textColors[0].value);
  const [borderColor, setBorderColor] = useState(colorsData.borderColors[0].value);
  const [selectedFontIndex, setSelectedFontIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [stlData, setStlData] = useState(null);
  const [font, setFont] = useState(null);
  const [loadingFont, setLoadingFont] = useState(true);
  
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const textMeshRef = useRef(null);
  
  // Define text color based on theme
  const themeTextColor = theme === 'light' ? '#333' : 'white';
  const labelColor = theme === 'light' ? '#9e9e9e' : '#c1c1c1';
  const cardBgColor = theme === 'light' ? 'white' : '#2d2d2d';
  
  // Custom styles for inputs in dark mode
  const inputStyle = theme === 'dark' ? {
    color: themeTextColor,
    borderBottomColor: '#666'
  } : {};
  
  const labelStyle = {
    color: labelColor
  };
  
  const cardStyle = {
    backgroundColor: cardBgColor
  };
  
  // Load font
  useEffect(() => {
    setLoadingFont(true);
    const fontLoader = new FontLoader();
    fontLoader.load(fontsData.fonts[selectedFontIndex].url, (loadedFont) => {
      setFont(loadedFont);
      setLoadingFont(false);
      
      // Track font loaded event
      amplitude.track('Font Loaded', {
        fontName: fontsData.fonts[selectedFontIndex].name,
        fontUrl: fontsData.fonts[selectedFontIndex].url
      });
    }, 
    // Progress callback
    undefined,
    // Error callback
    (error) => {
      console.error('Error loading font:', error);
      setLoadingFont(false);
      
      // Track font loading error
      amplitude.track('Font Loading Error', {
        fontName: fontsData.fonts[selectedFontIndex].name,
        fontUrl: fontsData.fonts[selectedFontIndex].url,
        error: error.message || 'Unknown error'
      });
    });
  }, [selectedFontIndex]);
  
  // Track page view
  useEffect(() => {
    amplitude.track('Page Viewed', {
      page: 'STL Generator',
      theme: theme
    });
  }, [theme]);
  
  // Setup Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;
    
    // Store ref value in a variable to use in cleanup
    const mountElement = mountRef.current;
    
    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);
    sceneRef.current = scene;
    
    // Create camera
    const camera = new THREE.PerspectiveCamera(75, mountElement.clientWidth / mountElement.clientHeight, 0.1, 1000);
    camera.position.z = 20;
    cameraRef.current = camera;
    
    // Create renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
    mountElement.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    
    // Add controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controlsRef.current = controls;
    
    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    scene.add(directionalLight);
    
    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();
    
    // Handle resize
    const handleResize = () => {
      if (!mountElement) return;
      camera.aspect = mountElement.clientWidth / mountElement.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mountElement && renderer.domElement) {
        mountElement.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);
  
  // Generate 3D text when parameters change
  useEffect(() => {
    if (!font || !sceneRef.current || text.trim() === '') return;
    
    // Remove previous text mesh
    if (textMeshRef.current) {
      sceneRef.current.remove(textMeshRef.current);
    }
    
    // Process text to remove spaces when border is off
    const processedText = !border ? text.replace(/\s+/g, '') : text;
    
    // Calculate bevel parameters that scale with font size to ensure consistent overlap
    const bevelThickness = border ? 0 : (0.6 * fontSize); // Increased bevel thickness for better solidity
    const bevelSize = border ? 0 : (0.3 * fontSize);      // Increased bevel size for better solidity
    
    // Create text geometry with adjusted parameters for better 3D printing
    const textGeometry = new TextGeometry(processedText, {
      font: font,
      size: fontSize,
      height: thickness,
      curveSegments: 24, // Increased for smoother curves
      bevelEnabled: !border, // Enable bevel when border is off for better letter connection
      bevelThickness: bevelThickness,
      bevelSize: bevelSize,
      bevelSegments: 8 // Increased for smoother bevels
    });
    
    textGeometry.computeBoundingBox();
    const textWidth = textGeometry.boundingBox.max.x - textGeometry.boundingBox.min.x;
    const textHeight = textGeometry.boundingBox.max.y - textGeometry.boundingBox.min.y;
    
    // Fixed 5mm keyring hole (converted to Three.js units)
    const holeRadius = 0.25; // 5mm diameter = 2.5mm radius, scaled down by factor of 10
    
    // Center text
    textGeometry.translate(
      -textWidth / 2,
      -textHeight / 2,
      -thickness / 2
    );
    
    const group = new THREE.Group();
    
    // Create text mesh with selected color
    const textMaterial = new THREE.MeshStandardMaterial({ 
      color: new THREE.Color(textColor) 
    });
    
    // Create the text mesh
    const textMesh = new THREE.Mesh(textGeometry, textMaterial);
    
    group.add(textMesh);
    
    // Add border if enabled with selected color
    if (border) {
      // Adjust border width to accommodate keyring hole
      const borderGeometry = new THREE.BoxGeometry(
        textWidth + borderSize * 2,
        textHeight + borderSize * 2,
        thickness
      );
      
      const borderMaterial = new THREE.MeshStandardMaterial({ 
        color: new THREE.Color(borderColor) 
      });
      const borderMesh = new THREE.Mesh(borderGeometry, borderMaterial);
      borderMesh.position.z = -thickness / 2;
      
      group.add(borderMesh);
      
      // Make sure text is in front of border
      textMesh.position.z = 0.01;
    }
    
    // Add keyring hole if enabled
    if (keyringHole) {
      // Define hole position constants - different for border/no border
      const holeOffsetX = border ? 
        (-textWidth / 2 - holeRadius * 3) : // With border: position further from text
        (-textWidth / 2 - holeRadius * 1.2); // No border: position closer to text
      const holeOffsetY = 0;
      
      // If no border, create a small backplane just for the hole
      if (!border) {
        const holePadSize = holeRadius * 4; // Smaller pad that's closer to text
        const holePadGeometry = new THREE.BoxGeometry(
          holePadSize,
          holePadSize,
          thickness * 1.2 // Slightly thicker pad for better solidity
        );
        
        const holePadMaterial = new THREE.MeshStandardMaterial({ 
          color: new THREE.Color(textColor) 
        });
        
        const holePadMesh = new THREE.Mesh(holePadGeometry, holePadMaterial);
        
        // Position the pad to be attached to the first letter
        holePadMesh.position.set(
          holeOffsetX + holeRadius, // Position closer to text
          holeOffsetY,
          0
        );
        
        group.add(holePadMesh);
        
        // Add a small connecting bridge for the no-border case
        const bridgeWidth = holeRadius * 2.5; // Wider bridge for better connection
        const bridgeHeight = holeRadius * 3.5; // Taller bridge for better visibility
        
        const bridgeGeometry = new THREE.BoxGeometry(
          bridgeWidth,
          bridgeHeight,
          thickness * 1.2 // Slightly thicker bridge for better solidity
        );
        
        const bridgeMaterial = new THREE.MeshStandardMaterial({ 
          color: new THREE.Color(textColor)
        });
        
        const bridgeMesh = new THREE.Mesh(bridgeGeometry, bridgeMaterial);
        
        // Position the bridge to connect the text and hole pad
        bridgeMesh.position.set(
          -textWidth / 2 - bridgeWidth/2 + 0.1, // More overlap with text
          0,
          0
        );
        
        group.add(bridgeMesh);
      }
      
      // Create a visual representation of the hole (will be subtracted in the STL)
      const holeVisual = new THREE.Mesh(
        new THREE.CylinderGeometry(holeRadius, holeRadius, thickness * 2, 32),
        new THREE.MeshStandardMaterial({ 
          color: 0x000000,
          transparent: true,
          opacity: 0.7
        })
      );
      holeVisual.rotation.x = Math.PI / 2;
      
      // Position the hole at the same position as in the preview
      holeVisual.position.set(
        holeOffsetX,
        holeOffsetY,
        0
      );
      
      group.add(holeVisual);
      
      // Add a connecting bridge if there's a border
      if (border) {
        // Create a connecting bridge between the text/border and the hole
        const bridgeWidth = holeRadius * 6;
        const bridgeHeight = holeRadius * 4;
        
        const bridgeGeometry = new THREE.BoxGeometry(
          bridgeWidth,
          bridgeHeight,
          thickness
        );
        
        const bridgeMaterial = new THREE.MeshStandardMaterial({ 
          color: new THREE.Color(border ? borderColor : textColor)
        });
        
        const bridgeMesh = new THREE.Mesh(bridgeGeometry, bridgeMaterial);
        
        // Position the bridge to connect the text and hole
        bridgeMesh.position.set(
          -textWidth / 2 - bridgeWidth / 2,
          0,
          0
        );
        
        group.add(bridgeMesh);
      }
    }
    
    sceneRef.current.add(group);
    textMeshRef.current = group;
    
    // Reset camera position to view the entire model
    const boundingBox = new THREE.Box3().setFromObject(group);
    const center = boundingBox.getCenter(new THREE.Vector3());
    const size = boundingBox.getSize(new THREE.Vector3());
    
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = cameraRef.current.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    
    cameraZ *= 1.5; // Add some margin
    cameraRef.current.position.z = cameraZ;
    
    // Update controls target to center of the object
    controlsRef.current.target.copy(center);
    controlsRef.current.update();
    
  }, [text, fontSize, thickness, border, borderSize, keyringHole, font, textColor, borderColor]);
  
  const generateSTL = () => {
    if (!textMeshRef.current) return;
    
    setGenerating(true);
    
    // Track STL generation start
    amplitude.track('STL Generation Started', {
      text: text,
      fontSize: fontSize,
      thickness: thickness,
      border: border,
      borderSize: borderSize,
      keyringHole: keyringHole,
      textColor: textColor,
      borderColor: borderColor,
      fontName: fontsData.fonts[selectedFontIndex].name
    });
    
    try {
      // Create a copy of the text mesh for CSG operations
      const textGroup = textMeshRef.current.clone();
      
      // If keyring hole is enabled, create a physical hole in the model
      if (keyringHole) {
        // Find all meshes in the group
        const meshes = [];
        textGroup.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            meshes.push(child);
          }
        });
        
        // Get text dimensions from the original mesh
        const textWidth = textMeshRef.current.children[0].geometry.boundingBox.max.x - 
                         textMeshRef.current.children[0].geometry.boundingBox.min.x;
        
        // Define hole position constants - same as in the preview
        const holeRadius = 0.25;
        const holeOffsetX = border ? 
          (-textWidth / 2 - holeRadius * 3) : // With border: position further from text
          (-textWidth / 2 - holeRadius * 1.2); // No border: position closer to text
        const holeOffsetY = 0;
        
        // Create a cylinder for the hole - make it larger to ensure it cuts through completely
        const holeGeometry = new THREE.CylinderGeometry(holeRadius, holeRadius, thickness * 10, 32);
        const holeMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff });
        const holeMesh = new THREE.Mesh(holeGeometry, holeMaterial);
        
        holeMesh.rotation.x = Math.PI / 2;
        
        // Position the hole at the same position as in the preview
        holeMesh.position.set(
          holeOffsetX,
          holeOffsetY,
          0
        );
        
        // First, ensure the hole mesh has proper matrix transformations
        holeMesh.updateMatrix();
        
        // Create a single merged geometry from all meshes for better CSG results
        const mergedGeometry = new THREE.BufferGeometry();
        const mergedMaterials = [];
        const geometries = [];
        
        // Collect all geometries and materials
        for (let i = 0; i < meshes.length; i++) {
          if (!meshes[i].geometry) continue;
          
          const clonedGeometry = meshes[i].geometry.clone();
          clonedGeometry.applyMatrix4(meshes[i].matrix);
          geometries.push(clonedGeometry);
          mergedMaterials.push(meshes[i].material);
        }
        
        // Use BufferGeometryUtils to merge if available
        if (geometries.length > 0) {
          try {
            // Try to import BufferGeometryUtils dynamically
            import('three/examples/jsm/utils/BufferGeometryUtils.js').then(({ BufferGeometryUtils }) => {
              const mergedGeom = BufferGeometryUtils.mergeBufferGeometries(geometries);
              const mergedMesh = new THREE.Mesh(mergedGeom, mergedMaterials[0]);
              
              // Apply CSG to the merged mesh
              const meshCSG = CSG.fromMesh(mergedMesh);
              const holeCSG = CSG.fromMesh(holeMesh);
              const resultCSG = meshCSG.subtract(holeCSG);
              const resultMesh = CSG.toMesh(resultCSG, new THREE.Matrix4(), mergedMesh.material);
              
              // Replace all children in the text group with our processed mesh
              while (textGroup.children.length > 0) {
                textGroup.remove(textGroup.children[0]);
              }
              
              textGroup.add(resultMesh);
              
              // Complete the STL export
              finalizeSTLExport(textGroup);
            }).catch(error => {
              console.error('Error importing BufferGeometryUtils:', error);
              processIndividualMeshes();
            });
          } catch (e) {
            console.error('Error merging geometries:', e);
            processIndividualMeshes();
          }
        } else {
          processIndividualMeshes();
        }
        
        // Fallback to processing individual meshes
        function processIndividualMeshes() {
          // Try a different approach - create a new group and add processed meshes to it
          const processedGroup = new THREE.Group();
          
          // Process each mesh individually for CSG operations
          for (let i = 0; i < meshes.length; i++) {
            try {
              // Skip any non-mesh objects or meshes without geometry
              if (!meshes[i].geometry) continue;
              
              // Clone the mesh to avoid modifying the original
              const workingMesh = meshes[i].clone();
              
              // Ensure matrices are properly applied
              workingMesh.updateMatrix();
              
              // Apply the mesh's world matrix to its geometry
              workingMesh.geometry = workingMesh.geometry.clone();
              workingMesh.geometry.applyMatrix4(workingMesh.matrix);
              
              // Reset the matrix
              workingMesh.position.set(0, 0, 0);
              workingMesh.rotation.set(0, 0, 0);
              workingMesh.scale.set(1, 1, 1);
              workingMesh.updateMatrix();
              
              // Create CSG objects with explicit positioning
              const meshCSG = CSG.fromMesh(workingMesh);
              const holeCSG = CSG.fromMesh(holeMesh);
              
              // Perform subtraction operation
              const resultCSG = meshCSG.subtract(holeCSG);
              
              // Convert back to mesh with identity matrix
              const resultMesh = CSG.toMesh(
                resultCSG,
                new THREE.Matrix4(),
                workingMesh.material.clone()
              );
              
              // Add the processed mesh to our new group
              processedGroup.add(resultMesh);
            } catch (e) {
              console.error('Error performing CSG operation on mesh:', e);
              // If CSG fails, add the original mesh to maintain the model
              processedGroup.add(meshes[i].clone());
            }
          }
          
          // Replace all children in the text group with our processed meshes
          while (textGroup.children.length > 0) {
            textGroup.remove(textGroup.children[0]);
          }
          
          // Add all processed meshes to the text group
          while (processedGroup.children.length > 0) {
            textGroup.add(processedGroup.children[0]);
          }
          
          // Complete the STL export
          finalizeSTLExport(textGroup);
        }
        
        return; // Early return since we'll call finalizeSTLExport after async operations
      } else {
        // If no keyring hole, proceed directly to export
        finalizeSTLExport(textGroup);
      }
      
      // Helper function to complete the STL export process
      function finalizeSTLExport(group) {
        const exporter = new STLExporter();
        const stl = exporter.parse(group, { binary: true });
        
        // Convert ArrayBuffer to Blob
        const blob = new Blob([stl], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        
        const filename = `${text.replace(/\s+/g, '_')}_keychain.stl`;
        
        setStlData({
          url,
          blob,
          filename
        });
        
        setGenerating(false);
        
        // Track STL generation success
        amplitude.track('STL Generation Completed', {
          text: text,
          fontSize: fontSize,
          thickness: thickness,
          border: border,
          borderSize: borderSize,
          keyringHole: keyringHole,
          textColor: textColor,
          borderColor: borderColor,
          fontName: fontsData.fonts[selectedFontIndex].name,
          filename: filename,
          fileSizeBytes: blob.size
        });
      }
    } catch (error) {
      console.error('Error generating STL:', error);
      setGenerating(false);
      
      // Track STL generation error
      amplitude.track('STL Generation Error', {
        text: text,
        fontSize: fontSize,
        thickness: thickness,
        border: border,
        borderSize: borderSize,
        keyringHole: keyringHole,
        textColor: textColor,
        borderColor: borderColor,
        fontName: fontsData.fonts[selectedFontIndex].name,
        error: error.message || 'Unknown error'
      });
    }
  };
  
  const downloadSTL = () => {
    if (!stlData) return;
    
    const link = document.createElement('a');
    link.href = stlData.url;
    link.download = stlData.filename;
    link.click();
    
    // Track STL download
    amplitude.track('STL Downloaded', {
      text: text,
      fontSize: fontSize,
      thickness: thickness,
      border: border,
      borderSize: borderSize,
      keyringHole: keyringHole,
      textColor: textColor,
      borderColor: borderColor,
      fontName: fontsData.fonts[selectedFontIndex].name,
      filename: stlData.filename,
      fileSizeBytes: stlData.blob.size
    });
  };
  
  return (
    <div className="stl-generator-container">
      <h1 className="center-align" style={{ color: themeTextColor }}>3D Text Keychain Generator</h1>
      
      <div className="row">
        <div className="col s12 m6">
          <div className="card" style={cardStyle}>
            <div className="card-content">
              <span className="card-title" style={{ color: themeTextColor }}>Design Your Keychain</span>
              
              <div className="input-field">
                <input
                  id="text-input"
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className={theme === 'dark' ? 'white-text' : ''}
                  style={inputStyle}
                />
                <label 
                  htmlFor="text-input" 
                  className={text ? "active" : ""} 
                  style={labelStyle}
                >
                  Enter text for your keychain
                </label>
              </div>
              
              {/* Font Selector */}
              <div className="input-field">
                <select
                  id="font-select"
                  className={`browser-default ${theme === 'dark' ? 'grey darken-3 white-text' : ''}`}
                  value={selectedFontIndex}
                  onChange={(e) => setSelectedFontIndex(parseInt(e.target.value))}
                  style={{ 
                    marginBottom: '20px',
                    padding: '8px',
                    borderRadius: '4px',
                    border: theme === 'dark' ? '1px solid #555' : '1px solid #ccc'
                  }}
                >
                  {fontsData.fonts.map((font, index) => (
                    <option key={font.name} value={index}>
                      {font.name}
                    </option>
                  ))}
                </select>
                <label 
                  htmlFor="font-select" 
                  className="active" 
                  style={{ 
                    color: themeTextColor,
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Font Style
                </label>
              </div>
              
              <div className="input-field">
                <p className="range-field">
                  <label style={{ color: themeTextColor }}>Font Size: {fontSize}</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.1"
                    value={fontSize}
                    onChange={(e) => setFontSize(parseFloat(e.target.value))}
                  />
                </p>
              </div>
              
              <div className="input-field">
                <p className="range-field">
                  <label style={{ color: themeTextColor }}>Thickness: {thickness}</label>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    value={thickness}
                    onChange={(e) => setThickness(parseFloat(e.target.value))}
                  />
                </p>
              </div>
              
              {/* Text Color Picker */}
              <div className="input-field">
                <div className="color-picker-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                  {colorsData.textColors.map((color) => (
                    <div
                      key={color.name}
                      className={`color-option ${textColor === color.value ? 'selected' : ''}`}
                      style={{
                        backgroundColor: color.value,
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        border: textColor === color.value ? '2px solid #26a69a' : '1px solid #ccc',
                        boxShadow: textColor === color.value ? '0 0 5px rgba(38, 166, 154, 0.5)' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => setTextColor(color.value)}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
              
              <div className="switch">
                <label style={{ color: themeTextColor }}>
                  Border Off
                  <input
                    type="checkbox"
                    checked={border}
                    onChange={() => setBorder(!border)}
                  />
                  <span className="lever"></span>
                  Border On
                </label>
              </div>
              
              {border && (
                <>
                  <div className="input-field" style={{ marginTop: '20px' }}>
                    <p className="range-field">
                      <label style={{ color: themeTextColor }}>Border Size: {borderSize}</label>
                      <input
                        type="range"
                        min="0.1"
                        max="2"
                        step="0.1"
                        value={borderSize}
                        onChange={(e) => setBorderSize(parseFloat(e.target.value))}
                      />
                    </p>
                  </div>
                  
                  {/* Border Color Picker */}
                  <div className="input-field">
                    <div className="color-picker-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                      {colorsData.borderColors.map((color) => (
                        <div
                          key={color.name}
                          className={`color-option ${borderColor === color.value ? 'selected' : ''}`}
                          style={{
                            backgroundColor: color.value,
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            border: borderColor === color.value ? '2px solid #26a69a' : '1px solid #ccc',
                            boxShadow: borderColor === color.value ? '0 0 5px rgba(38, 166, 154, 0.5)' : 'none',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => setBorderColor(color.value)}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              <div className="switch">
                <label style={{ color: themeTextColor }}>
                  Keyring Hole Off
                  <input
                    type="checkbox"
                    checked={keyringHole}
                    onChange={() => setKeyringHole(!keyringHole)}
                  />
                  <span className="lever"></span>
                  Keyring Hole On (5mm)
                </label>
              </div>
              
              <div className="card-action">
                <button
                  className="btn waves-effect waves-light"
                  onClick={generateSTL}
                  disabled={!text.trim() || generating || !font || loadingFont}
                >
                  {generating ? 'Generating...' : loadingFont ? 'Loading Font...' : 'Generate STL'}
                  <i className="material-icons right">3d_rotation</i>
                </button>
                
                {stlData && (
                  <button
                    className="btn waves-effect waves-light green right"
                    onClick={downloadSTL}
                  >
                    Download STL
                    <i className="material-icons right">file_download</i>
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Order Printing Button */}
          {stlData && (
            <div className="card" style={{...cardStyle, marginTop: '20px'}}>
              <div className="card-content">
                <span className="card-title" style={{ color: themeTextColor }}>Ready to Print?</span>
                <p style={{ color: themeTextColor, marginBottom: '20px' }}>
                  Order professional 3D printing of your custom keychain.
                </p>
                <button
                  className="btn waves-effect waves-light orange darken-2 btn-large"
                  style={{ width: '100%' }}
                  onClick={() => {
                    alert('Ordering functionality coming soon!');
                    
                    // Track order button click
                    amplitude.track('Order Button Clicked', {
                      text: text,
                      fontSize: fontSize,
                      thickness: thickness,
                      border: border,
                      borderSize: borderSize,
                      keyringHole: keyringHole,
                      textColor: textColor,
                      borderColor: borderColor,
                      fontName: fontsData.fonts[selectedFontIndex].name,
                      filename: stlData.filename
                    });
                  }}
                >
                  Order Printing
                  <i className="material-icons right">shopping_cart</i>
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="col s12 m6">
          <div className="card" style={cardStyle}>
            <div className="card-content">
              <span className="card-title" style={{ color: themeTextColor }}>3D Preview</span>
              <div 
                ref={mountRef} 
                style={{ 
                  width: '100%', 
                  height: '400px',
                  backgroundColor: theme === 'light' ? '#f0f0f0' : '#1a1a1a',
                  borderRadius: '4px'
                }}
              />
              <div className="center-align" style={{ marginTop: '10px' }}>
                <p className="grey-text" style={{ color: theme === 'dark' ? '#aaa' : '' }}>
                  <i className="material-icons tiny">info</i>
                  Drag to rotate, scroll to zoom
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StlGenerator;
